import { useCallback } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gqlErrors';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useWithPerimeterXCaptcha } from '#src/app/shared/hooks/perimeterX';
import type { ContactDecisionMakerReasonId } from '#src/app/shared/utils/contactDecisionMaker';

import type { DraftResult } from './api';
import { generateDecisionMakerMessageDraft, seededFallbackDraft } from './api';

type Params = {
	petitionId: string;
	decisionMakerId: string;
	decisionMakerName: string;
	reasonId: ContactDecisionMakerReasonId;
	story: string;
};

/**
 * Returns a caller that drafts the message. It never rejects and never surfaces an error state — see
 * {@link generateDecisionMakerMessageDraft} for why the supporter always ends up on the review screen.
 *
 * The mutation's path is PerimeterX-protected, so a challenge opens the captcha and retries once when
 * it is solved. An unsolved captcha, or a retry that is challenged again, yields the seeded draft.
 * Only the latter is reported: closing the captcha is the supporter's choice, not a fault.
 */
export function useGenerateDecisionMakerMessageDraft(): (params: Params) => Promise<DraftResult> {
	const utilityContext = useUtilityContext();
	const withPerimeterXCaptcha = useWithPerimeterXCaptcha('corgi_contact_dm_draft');

	return useCallback(
		async (params: Params) => {
			try {
				return await withPerimeterXCaptcha(async () => generateDecisionMakerMessageDraft(params, utilityContext));
			} catch (error) {
				if (!(error instanceof GQLResponsePerimeterXChallengeError)) {
					void utilityContext.errorReporter.report({
						error: error instanceof Error ? error : new Error(String(error)),
					});
				}
				return seededFallbackDraft(params, utilityContext.i18n);
			}
		},
		[withPerimeterXCaptcha, utilityContext],
	);
}
